import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import toast from "react-hot-toast";
import UpgradePrompt from "./UpgradePrompt";
import ApplyModal, { ApplyJob } from "./ApplyModal";
import "../styles/AdvancedJobSearch.css";

interface SavedSearch {
  id: string;
  name: string;
  query: any;
  alertEnabled: boolean;
  alertCadence: string;
  createdAt: string;
}

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  description: string;
  salary: string;
  salaryMin?: number;
  salaryMax?: number;
  url: string;
  postedAt: string;
  jobType: string;
  experienceLevel: string;
  isRemote: boolean;
  source?: string;
}

interface Filters {
  query: string;
  country: string;
  city: string;
  salaryMin: string;
  salaryMax: string;
  experienceLevel: string;
  jobType: string;
  remote: boolean;
  postedWithin: string;
  useUnifiedSearch: boolean;
}

const AdvancedJobSearch: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [savedJobs, setSavedJobs] = useState<Set<string>>(new Set());
  const [appliedJobs, setAppliedJobs] = useState<Set<string>>(new Set());
  const [applyJob, setApplyJob] = useState<ApplyJob | null>(null);
  const [showFilters, setShowFilters] = useState(true);
  
  const [filters, setFilters] = useState<Filters>({
    query: "",
    country: "gb",
    city: "",
    salaryMin: "",
    salaryMax: "",
    experienceLevel: "",
    jobType: "",
    remote: false,
    postedWithin: "",
    useUnifiedSearch: true,
  });
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [showSavedSearches, setShowSavedSearches] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState("");
  const [showBatchUpgradePrompt, setShowBatchUpgradePrompt] = useState(false);

  useEffect(() => {
    fetchSavedAndApplied();
    fetchSavedSearches();
    // Pre-populate query from URL param (e.g. from "Similar Job Opportunities")
    const qParam = searchParams.get("q");
    if (qParam) setFilters(f => ({ ...f, query: qParam }));
    // Pre-populate country/city from profile
    axiosInstance.get("/auth/profile").then(r => {
      const loc = r.data?.jobSearchLocation || {};
      const countryName: string = loc.country || "";
      const province: string = loc.province || "";
      const countryMap: Record<string, string> = {
        "United Kingdom": "gb", "UK": "gb", "United States": "us", "USA": "us",
        "Canada": "ca", "Australia": "au", "Germany": "de", "France": "fr",
        "Netherlands": "nl", "India": "in", "Singapore": "sg",
      };
      const code = countryMap[countryName] || "gb";
      setFilters(f => ({ ...f, country: code, city: province }));
    }).catch(() => {});
  }, [searchParams]);

  const fetchSavedAndApplied = async () => {
    try {
      const [savedRes, appliedRes] = await Promise.all([
        axiosInstance.get("/auth/jobs/saved"),
        axiosInstance.get("/auth/jobs/applications"),
      ]);
      setSavedJobs(new Set(savedRes.data.jobs.map((j: any) => j.adzunaJobId)));
      setAppliedJobs(new Set(appliedRes.data.applications.map((a: any) => a.adzunaJobId)));
    } catch (error) {
      console.error("Failed to fetch saved/applied jobs");
    }
  };

  const fetchSavedSearches = async () => {
    try {
      const res = await axiosInstance.get("/saved-searches");
      setSavedSearches(res.data.items || []);
    } catch { /* silently ignore */ }
  };

  const handleSaveSearch = async () => {
    const name = saveSearchName.trim() || filters.query || "Saved Search";
    try {
      await axiosInstance.post("/saved-searches", { name, query: filters });
      toast.success("Search saved!");
      setSaveSearchName("");
      fetchSavedSearches();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to save search");
    }
  };

  const handleLoadSearch = (s: SavedSearch) => {
    if (s.query && typeof s.query === "object") {
      setFilters({ ...filters, ...s.query });
      toast.success(`Loaded: ${s.name}`);
      setShowSavedSearches(false);
    }
  };

  const handleDeleteSavedSearch = async (id: string) => {
    try {
      await axiosInstance.delete(`/saved-searches/${id}`);
      setSavedSearches(prev => prev.filter(s => s.id !== id));
    } catch { toast.error("Failed to delete saved search"); }
  };

  const handleSearch = async () => {
    if (!filters.query.trim()) {
      toast.error("Please enter a search query");
      return;
    }
    if (!/^[a-z]{2}$/i.test(filters.country)) {
      toast.error("Please select a valid country");
      return;
    }
    const min = filters.salaryMin ? Number(filters.salaryMin) : undefined;
    const max = filters.salaryMax ? Number(filters.salaryMax) : undefined;
    if (min !== undefined && (Number.isNaN(min) || min < 0)) {
      toast.error("Minimum salary must be a positive number");
      return;
    }
    if (max !== undefined && (Number.isNaN(max) || max < 0)) {
      toast.error("Maximum salary must be a positive number");
      return;
    }
    if (min !== undefined && max !== undefined && min > max) {
      toast.error("Minimum salary cannot exceed maximum salary");
      return;
    }

    try {
      setLoading(true);
      const params: any = {
        query: filters.query,
        country: filters.country,
        limit: 30,
      };

      if (filters.city) params.where = filters.city;
      if (filters.salaryMin) params.salaryMin = filters.salaryMin;
      if (filters.salaryMax) params.salaryMax = filters.salaryMax;
      if (filters.experienceLevel) params.experienceLevel = filters.experienceLevel;
      if (filters.jobType) params.jobType = filters.jobType;
      if (filters.remote) params.remote = "true";
      if (filters.postedWithin) params.postedWithin = filters.postedWithin;
      setSelectedJobs(new Set());

      const endpoint = filters.useUnifiedSearch ? "/auth/jobs/unified-search" : "/auth/jobs/search-advanced";
      const response = await axiosInstance.get(endpoint, { params });
      setJobs(response.data.jobs);
      
      if (response.data.jobs.length === 0) {
        toast("No jobs found. Try adjusting your filters.", { icon: "🔍" });
      } else {
        toast.success(`Found ${response.data.jobs.length} jobs`);
      }
    } catch (error) {
      toast.error("Failed to search jobs");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveJob = async (job: Job) => {
    try {
      await axiosInstance.post("/auth/jobs/save", {
        adzunaJobId: job.id,
        jobTitle: job.title,
        company: job.company,
        location: job.location,
        salary: job.salary,
        url: job.url,
        description: job.description,
      });
      setSavedJobs(prev => new Set(Array.from(prev).concat(job.id)));
      toast.success("Job saved!");
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to save job");
    }
  };

  // Open the Track modal so the user can confirm the resume used before the
  // application is recorded with full info.
  const handleApplyJob = (job: Job) => {
    setApplyJob({
      id: job.id,
      title: job.title,
      company: job.company,
      location: job.location,
      url: job.url,
      description: job.description,
    });
  };

  const markApplied = (jobId: string) => {
    setAppliedJobs(prev => new Set(Array.from(prev).concat(jobId)));
  };

  const clearFilters = () => {
    setFilters({
      query: filters.query,
      country: "gb",
      city: "",
      salaryMin: "",
      salaryMax: "",
      experienceLevel: "",
      jobType: "",
      remote: false,
      postedWithin: "",
      useUnifiedSearch: true,
    });
  };

  const handleBatchApply = async () => {
    const toApply = jobs.filter(j => selectedJobs.has(j.id) && !appliedJobs.has(j.id));
    if (toApply.length === 0) { toast("All selected jobs are already saved or applied."); return; }
    // Batch apply can't prompt per job, so attach the user's primary resume.
    let primaryResumeId: string | undefined;
    try {
      const resumeRes = await axiosInstance.get("/auth/resumes");
      const resumeList: Array<{ id: string; isPrimary: boolean }> = resumeRes.data?.resumes || [];
      primaryResumeId = (resumeList.find(r => r.isPrimary) || resumeList[0])?.id;
    } catch { /* proceed without a resume if the lookup fails */ }
    let success = 0;
    for (const job of toApply) {
      try {
        await axiosInstance.post("/auth/jobs/apply", {
          adzunaJobId: job.id, jobTitle: job.title, company: job.company,
          location: job.location, url: job.url, description: job.description || "",
          resumeUsedId: primaryResumeId,
        });
        setAppliedJobs(prev => new Set(Array.from(prev).concat(job.id)));
        success++;
      } catch (err: any) {
        if (err.response?.status === 403 && err.response?.data?.upgradeRequired) {
          setShowBatchUpgradePrompt(true);
          break;
        }
      }
    }
    if (success > 0) toast.success(`Marked ${success} job${success !== 1 ? "s" : ""} as applied!`);
    setSelectedJobs(new Set());
  };

  const toggleSelectJob = (id: string) => {
    setSelectedJobs(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const countries = [
    { code: "gb", name: "United Kingdom" },
    { code: "us", name: "United States" },
    { code: "ca", name: "Canada" },
    { code: "au", name: "Australia" },
    { code: "de", name: "Germany" },
    { code: "fr", name: "France" },
    { code: "nl", name: "Netherlands" },
    { code: "in", name: "India" },
    { code: "sg", name: "Singapore" },
  ];

  return (
    <div className="ajs-container">
      {showBatchUpgradePrompt && (
        <UpgradePrompt
          feature="batch_apply"
          message="Batch Apply is a Pro feature. Upgrade to track multiple applications at once."
          onDismiss={() => setShowBatchUpgradePrompt(false)}
        />
      )}

      <div className="ajs-header">
        <button onClick={() => navigate("/dashboard")} className="ajs-back-btn">
          ← Back to Dashboard
        </button>
        <h1>🔍 Advanced Job Search</h1>
        <p>Search with filters for salary, experience level, job type, and more</p>
      </div>

      {/* Search Bar */}
      <div className="ajs-search-bar">
        <input
          type="text"
          value={filters.query}
          onChange={(e) => setFilters({ ...filters, query: e.target.value })}
          placeholder="Job title, skills, or keywords..."
          className="ajs-search-input"
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
        />
        <button onClick={handleSearch} className="ajs-search-btn" disabled={loading}>
          {loading ? "Searching..." : "🔍 Search"}
        </button>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="ajs-filter-toggle"
        >
          {showFilters ? "Hide Filters" : "Show Filters"} ⚙️
        </button>
        <button
          onClick={() => setShowSavedSearches(!showSavedSearches)}
          className="ajs-filter-toggle"
          title="Saved searches"
        >
          📋 Saved {savedSearches.length > 0 && `(${savedSearches.length})`}
        </button>
      </div>

      {/* Save current search */}
      {filters.query && (
        <div className="ajs-save-search-bar">
          <input
            type="text"
            value={saveSearchName}
            onChange={e => setSaveSearchName(e.target.value)}
            placeholder={`Save as "${filters.query}" or enter a name…`}
            className="ajs-input"
            style={{ flex: 1 }}
            onKeyPress={e => e.key === "Enter" && handleSaveSearch()}
          />
          <button onClick={handleSaveSearch} className="ajs-search-btn" style={{ whiteSpace: "nowrap" }}>
            💾 Save Search
          </button>
        </div>
      )}

      {/* Saved searches list */}
      {showSavedSearches && (
        <div className="ajs-saved-searches">
          <h3>Saved Searches</h3>
          {savedSearches.length === 0 ? (
            <p className="ajs-empty-sub">No saved searches yet.</p>
          ) : (
            <ul className="ajs-saved-list">
              {savedSearches.map(s => (
                <li key={s.id} className="ajs-saved-item">
                  <button className="ajs-saved-load" onClick={() => handleLoadSearch(s)}>
                    <span className="ajs-saved-name">{s.name}</span>
                    <span className="ajs-saved-meta">{(s.query as any)?.query || ""}</span>
                  </button>
                  <button className="ajs-saved-delete" onClick={() => handleDeleteSavedSearch(s.id)} title="Delete">
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="ajs-filters">
          <div className="ajs-filters-grid">
            <div className="ajs-filter-group">
              <label>Country</label>
              <select
                value={filters.country}
                onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                className="ajs-select"
              >
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="ajs-filter-group">
              <label>City / Province</label>
              <input
                type="text"
                value={filters.city}
                onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                placeholder="e.g. Toronto, Ontario"
                className="ajs-input"
              />
            </div>

            <div className="ajs-filter-group">
              <label>Experience Level</label>
              <select
                value={filters.experienceLevel}
                onChange={(e) => setFilters({ ...filters, experienceLevel: e.target.value })}
                className="ajs-select"
              >
                <option value="">All Levels</option>
                <option value="junior">Junior / Entry Level</option>
                <option value="mid">Mid Level</option>
                <option value="senior">Senior / Lead</option>
              </select>
            </div>

            <div className="ajs-filter-group">
              <label>Job Type</label>
              <select
                value={filters.jobType}
                onChange={(e) => setFilters({ ...filters, jobType: e.target.value })}
                className="ajs-select"
              >
                <option value="">All Types</option>
                <option value="full-time">Full-time</option>
                <option value="part-time">Part-time</option>
                <option value="contract">Contract</option>
                <option value="internship">Internship</option>
              </select>
            </div>

            <div className="ajs-filter-group">
              <label>Posted Within</label>
              <select
                value={filters.postedWithin}
                onChange={(e) => setFilters({ ...filters, postedWithin: e.target.value })}
                className="ajs-select"
              >
                <option value="">Any Time</option>
                <option value="1">Last 24 hours</option>
                <option value="3">Last 3 days</option>
                <option value="7">Last week</option>
                <option value="14">Last 2 weeks</option>
                <option value="30">Last month</option>
              </select>
            </div>

            <div className="ajs-filter-group">
              <label>Min Salary</label>
              <input
                type="number"
                value={filters.salaryMin}
                onChange={(e) => setFilters({ ...filters, salaryMin: e.target.value })}
                placeholder="e.g., 50000"
                className="ajs-input"
              />
            </div>

            <div className="ajs-filter-group">
              <label>Max Salary</label>
              <input
                type="number"
                value={filters.salaryMax}
                onChange={(e) => setFilters({ ...filters, salaryMax: e.target.value })}
                placeholder="e.g., 100000"
                className="ajs-input"
              />
            </div>
          </div>

          <div className="ajs-filter-actions">
            <label className="ajs-checkbox-label">
              <input
                type="checkbox"
                checked={filters.remote}
                onChange={(e) => setFilters({ ...filters, remote: e.target.checked })}
              />
              🏠 Remote Only
            </label>
            <label className="ajs-checkbox-label">
              <input
                type="checkbox"
                checked={filters.useUnifiedSearch}
                onChange={(e) => setFilters({ ...filters, useUnifiedSearch: e.target.checked })}
              />
              🔗 Search All Boards
            </label>
            <button onClick={() => navigate('/job-board-settings')} className="ajs-boards-btn">
              ⚙️ Configure Boards
            </button>
            <button onClick={clearFilters} className="ajs-clear-btn">
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="ajs-results">
        {loading ? (
          <div className="ajs-loading">
            <div className="ajs-spinner" />
            <p>Searching jobs...</p>
          </div>
        ) : jobs.length > 0 ? (
          <>
            <div className="ajs-results-header">
              <span>{jobs.length} jobs found</span>
              {jobs.length >= 30 && (
                <span className="ajs-results-limit-note">
                  ℹ️ Showing up to 30 results per search. Use filters to narrow your results.
                </span>
              )}
            </div>
            {selectedJobs.size > 0 && (
              <div className="ajs-batch-bar">
                <span>{selectedJobs.size} job{selectedJobs.size !== 1 ? "s" : ""} selected</span>
                <button onClick={handleBatchApply} className="ajs-batch-apply-btn">
                  📤 Mark Selected as Applied
                </button>
                <button onClick={() => setSelectedJobs(new Set())} className="ajs-batch-clear-btn">
                  Clear Selection
                </button>
              </div>
            )}
            <div className="ajs-jobs-grid">
              {jobs.map((job) => (
                <div key={job.id} className={`ajs-job-card${selectedJobs.has(job.id) ? " selected" : ""}`}>
                  <label className="ajs-job-select-wrap">
                    <input
                      type="checkbox"
                      checked={selectedJobs.has(job.id)}
                      onChange={() => toggleSelectJob(job.id)}
                      className="ajs-job-select"
                    />
                    <span className="ajs-job-select-label">Select</span>
                  </label>
                  <div className="ajs-job-header">
                    <h3 className="ajs-job-title">{job.title}</h3>
                    <div className="ajs-job-badges">
                      {job.source && (
                        <span className={`ajs-badge ajs-badge-source ajs-badge-${job.source}`}>
                          {job.source}
                        </span>
                      )}
                      <span className={`ajs-badge ajs-badge-level ${job.experienceLevel.toLowerCase()}`}>
                        {job.experienceLevel}
                      </span>
                      <span className={`ajs-badge ajs-badge-type`}>
                        {job.jobType}
                      </span>
                      {job.isRemote && (
                        <span className="ajs-badge ajs-badge-remote">🏠 Remote</span>
                      )}
                    </div>
                  </div>

                  <p className="ajs-job-company">{job.company}</p>
                  <p className="ajs-job-location">📍 {job.location}</p>

                  <p className="ajs-job-description">
                    {job.description.substring(0, 200)}
                    {job.description.length > 200 ? "..." : ""}
                  </p>

                  <div className="ajs-job-meta">
                    <span className="ajs-job-salary">💰 {job.salary}</span>
                    <span className="ajs-job-posted">
                      📅 {new Date(job.postedAt).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="ajs-job-actions">
                    <button
                      onClick={() => handleSaveJob(job)}
                      disabled={savedJobs.has(job.id)}
                      className={`ajs-action-btn ${savedJobs.has(job.id) ? "saved" : "save"}`}
                    >
                      {savedJobs.has(job.id) ? "✓ Saved" : "📌 Save"}
                    </button>
                    <button
                      onClick={() => handleApplyJob(job)}
                      disabled={appliedJobs.has(job.id)}
                      className={`ajs-action-btn ${appliedJobs.has(job.id) ? "applied" : "apply"}`}
                    >
                      {appliedJobs.has(job.id) ? "✓ Applied" : "📤 Track"}
                    </button>
                    <button
                      onClick={() => window.open(job.url, "_blank")}
                      className="ajs-action-btn view"
                    >
                      View Job →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="ajs-empty">
            <p>🔍 Enter a search query and click "Search" to find jobs</p>
            <p className="ajs-empty-sub">Use filters to narrow down your results</p>
          </div>
        )}
      </div>

      {applyJob && (
        <ApplyModal
          job={applyJob}
          onClose={() => setApplyJob(null)}
          onApplied={markApplied}
        />
      )}
    </div>
  );
};

export default AdvancedJobSearch;
